/**
 * Generic base class for promise-based worker communication.
 *
 * Manages request ID generation, pending request tracking, and message dispatch.
 * Subclasses implement `handleResponse()` for domain-specific deserialization.
 *
 * Usage:
 *   class MyWorkerClient extends WorkerClient<MyResolveType> {
 *     protected handleResponse(response: WorkerMessage, pending: PendingRequest<MyResolveType>): void {
 *       // domain-specific response handling
 *     }
 *   }
 */

/** Standard message shape for worker communication (both directions) */
export interface WorkerMessage<T = unknown> {
  id: string;
  type: string;
  data?: T;
  error?: string;
}

/** A pending request stored in the map, waiting for a worker response */
export interface PendingRequest<TResolve> {
  resolve: (value: TResolve) => void;
  reject: (error: Error) => void;
  type: string;
  /** Subclasses can attach extra fields (e.g. callbacks) via the extras parameter in sendRequest */
  [key: string]: unknown;
}

export abstract class WorkerClient<TResolve = unknown> {
  worker: Worker | null = null;
  private requestIdCounter = 0;
  pendingRequests = new Map<string, PendingRequest<TResolve>>();
  isInitialized = false;

  constructor(workerUrlOrWorker: URL | Worker, workerOptions?: WorkerOptions) {
    this.createWorker(workerUrlOrWorker, workerOptions);
  }

  private createWorker(workerUrlOrWorker: URL | Worker, workerOptions?: WorkerOptions): void {
    try {
      this.worker = workerUrlOrWorker instanceof Worker
        ? workerUrlOrWorker
        : new Worker(workerUrlOrWorker, workerOptions ?? { type: 'module' });

      this.worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
        const response = event.data;
        const pending = this.pendingRequests.get(response.id);

        if (pending) {
          this.pendingRequests.delete(response.id);
          this.handleResponse(response, pending);
        }
      };

      this.worker.onerror = (error: ErrorEvent) => {
        this.onWorkerError(error);
      };
    } catch (error) {
      console.error(`[WorkerClient] Failed to create worker:`, error);
      this.worker = null;
    }
  }

  /**
   * Called when the worker encounters an error.
   * Default: rejects all pending requests and resets initialization state.
   * Subclasses can override for custom error handling.
   */
  protected onWorkerError(error: ErrorEvent): void {
    console.error('[WorkerClient] Worker error:', error);
    this.pendingRequests.forEach(({ reject }) => {
      reject(new Error('Worker crashed'));
    });
    this.pendingRequests.clear();
    this.isInitialized = false;
  }

  /**
   * Send a typed request to the worker and return a promise.
   *
   * @param type The message type string
   * @param data Optional data payload
   * @param extras Extra fields to attach to the pending request (e.g. callbacks)
   * @returns Promise that resolves when the worker responds to this request ID
   */
  sendRequest<T extends TResolve>(
    type: string,
    data?: unknown,
    extras?: Partial<PendingRequest<TResolve>>,
  ): Promise<T> {
    if (!this.worker) {
      return Promise.reject(new Error('Worker not created'));
    }

    const id = `req_${this.requestIdCounter++}`;

    return new Promise<T>((resolve, reject) => {
      const pending: PendingRequest<TResolve> = {
        resolve: resolve as (value: TResolve) => void,
        reject,
        type,
        ...extras,
      };

      this.pendingRequests.set(id, pending);

      const message: WorkerMessage = { id, type, data };
      this.worker?.postMessage(message);
    });
  }

  /**
   * Handle a worker response message. Called after the pending request is
   * looked up and removed from the map.
   *
   * Subclasses MUST implement this to handle domain-specific response types.
   */
  protected abstract handleResponse(
    response: WorkerMessage,
    pending: PendingRequest<TResolve>,
  ): void;

  /**
   * Terminate the worker and reject/resolve all pending requests.
   * Subclasses can override to add custom cleanup.
   */
  terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.pendingRequests.clear();
    this.isInitialized = false;
  }
}
